import { useMemo } from 'react';
import { Button, Dialog, Group, IconDisabled, Token, Typography } from '@screentone/core';

import { useAllessehContentUpdate } from 'contexts/allesseh-content-update/useAllessehContentUpdate';
import { useSummarianContext } from 'contexts/summarian/useSummarianContext';
import { ArticleItem } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { formatInTimeZone } from 'utils/dates';
import { safelyParseContent } from 'utils/temp';
import styles from './EmbargoPagePublishDialog.module.scss';

import { useAltSummFields } from 'contexts/summarian/summarian.utils';

interface EmbargoPagePublishDialogProps {
  onPublish: () => void;
  onCancel: () => void;
  embargoedContent: ArticleItem[];
}

const EmbargoedArticle = ({ embargoedContent }: { embargoedContent: AllessehContent }) => {
  const { selectedAltSummVariant: altSummVariant } = useSummarianContext();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const updatedContent = useAllessehContentUpdate(embargoedContent.data.id, { initialContent: embargoedContent });
  const allessehContent = useMemo(() => updatedContent ?? embargoedContent, [embargoedContent, updatedContent]);

  const {
    data: {
      attributes: { embargo_datetime_utc: embargoDatetime }
    }
  } = allessehContent;
  const modifiedDate = embargoDatetime
    ? formatInTimeZone(
        embargoDatetime,
        publicationSettingsResp?.publicationSetting.preferredTimezone,
        "LLLL dd, yyyy 'at' hh:mm aaaa",
        { showTimezoneAbbreviation: true }
      )
    : new Date();
  const { headline } = useAltSummFields(allessehContent, altSummVariant);
  return (
    <div className={styles.embargoedArticle}>
      <Typography variant="header4" weight="bold" margin={{ bottom: 'xs' }}>
        {headline}
      </Typography>
      <Group gap="sm">
        <Token className={styles.token} icon={IconDisabled} color="lava" />
        <Typography color="asphalt">{modifiedDate}</Typography>
      </Group>
    </div>
  );
};

export const EmbargoPagePublishDialog = ({ onPublish, onCancel, embargoedContent }: EmbargoPagePublishDialogProps) => (
  <Dialog status="open" className={styles.dialog} onDismiss={onCancel} focusTrap={false}>
    <Dialog.Title data-testid="embargo-dialog-title">Publish With Embargo</Dialog.Title>
    <Dialog.Content>
      <Typography color="asphalt" size="sm" data-testid="embargo-dialog-header">
        This page contains an embargoed story. The page content will update at the scheduled publish time of the
        following articles:
      </Typography>
      <br />
      <div className={styles.embargoedArticles} data-testid="embargo-date-scheduled">
        {embargoedContent.map((article) => {
          const content = safelyParseContent<AllessehContent>(article);
          return <EmbargoedArticle key={content.data.id} embargoedContent={content} />;
        })}
      </div>
    </Dialog.Content>
    <Dialog.Actions>
      <Button secondary onClick={onCancel}>
        Cancel
      </Button>
      <Button
        data-testid="embargo-publish-button"
        primary
        onClick={() => {
          onCancel();
          onPublish();
        }}
      >
        Publish
      </Button>
    </Dialog.Actions>
  </Dialog>
);
